import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useWedding } from '../contexts/WeddingContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Edit, Trash, Filter } from 'iconoir-react';

export default forwardRef(function ExpensesPage({ bare = false }, ref) {
  const { 
    currentWedding, expenses, events, vendors, categories,
    fetchExpenses, createExpense, updateExpense, deleteExpense,
    fetchEvents, fetchVendors, fetchCategories, createVendor
  } = useWedding();
  
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterEvent, setFilterEvent] = useState('all');
  const [formData, setFormData] = useState({
    category: '',
    description: '',
    amount: '',
    event_id: '',
    vendor_id: '',
    paid: false
  });
  // Inline-vendor-create state — when the user picks "+ Add new vendor" from
  // the vendor dropdown, we expose two more inputs (name + category) and a
  // "Save vendor" button. On success we auto-select the new vendor.
  const [inlineVendor, setInlineVendor] = useState({ open: false, name: '', category: '', saving: false });

  useEffect(() => {
    if (currentWedding) {
      fetchExpenses(currentWedding.id);
      fetchEvents(currentWedding.id);
      fetchVendors(currentWedding.id);
      fetchCategories();
    }
  }, [currentWedding, fetchExpenses, fetchEvents, fetchVendors, fetchCategories]);

  const handleOpenModal = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        category: expense.category,
        description: expense.description,
        amount: expense.amount?.toString() || '',
        event_id: expense.event_id || '',
        vendor_id: expense.vendor_id || '',
        paid: expense.paid || false
      });
    } else {
      setEditingExpense(null);
      setFormData({
        category: '',
        description: '',
        amount: '',
        event_id: '',
        vendor_id: '',
        paid: false
      });
    }
    setShowModal(true);
    setInlineVendor({ open: false, name: '', category: '', saving: false });
  };

  // Reset inline form whenever the modal closes (avoids stale state on next open).
  const handleCloseModal = (open) => {
    setShowModal(open);
    if (!open) setInlineVendor({ open: false, name: '', category: '', saving: false });
  };

  // "+ Add new vendor" handler — switches the vendor select to an inline form.
  const handleVendorSelect = (value) => {
    if (value === '__new__') {
      setInlineVendor((prev) => ({ ...prev, open: true, name: '', category: '' }));
      // keep formData.vendor_id at its previous value until the new vendor is saved
      return;
    }
    setInlineVendor((prev) => ({ ...prev, open: false }));
    setFormData({ ...formData, vendor_id: value === 'none' ? '' : value });
  };

  const handleSaveInlineVendor = async () => {
    if (!inlineVendor.name.trim() || !inlineVendor.category.trim()) return;
    setInlineVendor((prev) => ({ ...prev, saving: true }));
    try {
      const created = await createVendor({
        wedding_id: currentWedding.id,
        name: inlineVendor.name.trim(),
        category: inlineVendor.category.trim(),
      });
      // createVendor returns the new doc — auto-select it.
      const newId = created?.id || created?._id;
      if (newId) {
        setFormData((prev) => ({ ...prev, vendor_id: newId }));
      }
      // Refresh vendor list so the dropdown shows the new entry.
      await fetchVendors(currentWedding.id);
      setInlineVendor({ open: false, name: '', category: '', saving: false });
    } catch (err) {
      console.error('Failed to create vendor', err);
      setInlineVendor((prev) => ({ ...prev, saving: false }));
    }
  };

  // Exposed for parent (BudgetPage) so the "Quick add expense" button on the
  // pie chart card can trigger the existing modal without duplicating the
  // Add Expense form. Always opens the create-new flow (never edit).
  useImperativeHandle(ref, () => ({
    openAddModal: () => handleOpenModal(),
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const expenseData = {
      wedding_id: currentWedding.id,
      category: formData.category,
      description: formData.description,
      amount: parseFloat(formData.amount) || 0,
      currency: currentWedding.preferred_currency,
      event_id: formData.event_id || null,
      vendor_id: formData.vendor_id || null,
      paid: formData.paid
    };

    if (editingExpense) {
      await updateExpense(editingExpense.id, expenseData);
    } else {
      await createExpense(expenseData);
    }
    setShowModal(false);
    fetchExpenses(currentWedding.id);
  };

  const handleDelete = async (expenseId) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      await deleteExpense(expenseId);
    }
  };

  const handleTogglePaid = async (expense) => {
    await updateExpense(expense.id, { paid: !expense.paid });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currentWedding?.preferred_currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const filteredExpenses = expenses.filter(expense => {
    if (filterCategory !== 'all' && expense.category !== filterCategory) return false;
    if (filterEvent !== 'all' && expense.event_id !== filterEvent) return false;
    return true;
  });

  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const paidExpenses = filteredExpenses.filter(e => e.paid).reduce((sum, e) => sum + (e.amount || 0), 0);

  const getEventName = (eventId) => {
    const event = events.find(e => e.id === eventId);
    return event?.name || '-';
  };

  const getVendorName = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor?.name || '-';
  };

  if (!currentWedding) {
    const empty = (
      <div className="text-center py-12 text-muted">
        Please select or create a wedding first.
      </div>
    );
    return bare ? empty : <Layout>{empty}</Layout>;
  }

  const content = (
    <div className="space-y-8" data-testid="expenses-page">
        {/* Header — hidden in bare mode (the parent Budget page owns its own header) */}
        {!bare && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="font-serif font-light text-4xl sm:text-5xl text-charcoal tracking-tight">
                Expenses
              </h1>
              <p className="text-muted font-light mt-2">
                Track and manage all wedding expenses
              </p>
            </div>
            <Button 
              onClick={() => handleOpenModal()}
              className="btn-primary flex items-center gap-2"
              data-testid="add-expense-btn"
            >
              <Plus className="w-4 h-4" />
              Add Expense
            </Button>
          </div>
        )}
        {bare && (
          <div className="flex justify-end">
            <Button 
              onClick={() => handleOpenModal()}
              className="btn-primary flex items-center gap-2"
              data-testid="add-expense-btn"
            >
              <Plus className="w-4 h-4" />
              Add Expense
            </Button>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="card-surface p-6">
            <p className="text-sm text-muted uppercase tracking-wider mb-1">Total Committed</p>
            <p className="font-mono text-2xl text-charcoal tracking-tight" data-testid="total-expenses">
              {formatCurrency(totalExpenses)}
            </p>
          </Card>
          <Card className="card-surface p-6">
            <p className="text-sm text-muted uppercase tracking-wider mb-1">Paid</p>
            <p className="font-mono text-2xl text-moss tracking-tight" data-testid="paid-expenses">
              {formatCurrency(paidExpenses)}
            </p>
          </Card>
          <Card className="card-surface p-6">
            <p className="text-sm text-muted uppercase tracking-wider mb-1">Awaiting Payment</p>
            <p className="font-mono text-2xl text-ochre tracking-tight" data-testid="unpaid-expenses">
              {formatCurrency(totalExpenses - paidExpenses)}
            </p>
          </Card>
        </div>

        {/* Filters */}
        <Card className="card-surface p-4">
          <div className="flex flex-wrap items-center gap-4">
            <Filter className="w-4 h-4 text-muted" />
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted">Category:</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-40 h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted">Event:</Label>
              <Select value={filterEvent} onValueChange={setFilterEvent}>
                <SelectTrigger className="w-40 h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  {events.map(event => (
                    <SelectItem key={event.id} value={event.id}>{event.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Expenses Table */}
        <Card className="card-surface overflow-hidden">
          {filteredExpenses.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-muted mb-4">No expenses found</p>
              <Button 
                onClick={() => handleOpenModal()}
                className="btn-primary"
                data-testid="create-first-expense-btn"
              >
                Add Your First Expense
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border">
                  <TableHead className="text-muted uppercase text-xs tracking-wider">Paid</TableHead>
                  <TableHead className="text-muted uppercase text-xs tracking-wider">Category</TableHead>
                  <TableHead className="text-muted uppercase text-xs tracking-wider">Description</TableHead>
                  <TableHead className="text-muted uppercase text-xs tracking-wider">Event</TableHead>
                  <TableHead className="text-muted uppercase text-xs tracking-wider text-right">Amount</TableHead>
                  <TableHead className="text-muted uppercase text-xs tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredExpenses.map((expense) => (
                  <TableRow key={expense.id} className="border-b border-border" data-testid={`expense-row-${expense.id}`}>
                    <TableCell>
                      <Checkbox
                        checked={expense.paid}
                        onCheckedChange={() => handleTogglePaid(expense)}
                        data-testid={`expense-paid-${expense.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-sans text-charcoal">{expense.category}</TableCell>
                    <TableCell className="text-muted max-w-xs">
                      <div className="flex items-center gap-2 truncate">
                        <span className="truncate">{expense.description}</span>
                        {expense.auto_from_payment_id && (
                          <span
                            title="This expense was auto-created from a payment plan. Edit it on the Payments tab to keep everything in sync."
                            className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 bg-ochre/15 text-ochre border border-ochre/30 text-[9px] uppercase tracking-widest font-mono rounded-sm"
                            data-testid={`expense-auto-pill-${expense.id}`}
                          >
                            From payment
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted">{getEventName(expense.event_id)}</TableCell>
                    <TableCell className="text-right font-mono text-charcoal">
                      {formatCurrency(expense.amount)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleOpenModal(expense)}
                          className="p-2 text-muted hover:text-charcoal transition-colors"
                          data-testid={`edit-expense-${expense.id}`}
                        >
                          <Edit className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => handleDelete(expense.id)}
                          className="p-2 text-muted hover:text-terracotta transition-colors"
                          data-testid={`delete-expense-${expense.id}`}
                        >
                          <Trash className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* Add/Edit Modal */}
        <Dialog open={showModal} onOpenChange={handleCloseModal}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                {editingExpense ? 'Edit Expense' : 'Add New Expense'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5 mt-4">
              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="input-field h-12" data-testid="expense-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-field h-12"
                  placeholder="What is this expense for?"
                  required
                  data-testid="expense-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Amount</Label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="input-field h-12 font-mono"
                  placeholder="0"
                  min="0"
                  step="0.01"
                  required
                  data-testid="expense-amount-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Event (Optional)</Label>
                <Select 
                  value={formData.event_id || "none"} 
                  onValueChange={(value) => setFormData({ ...formData, event_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="input-field h-12" data-testid="expense-event-select">
                    <SelectValue placeholder="Select event" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific event</SelectItem>
                    {events.map(event => (
                      <SelectItem key={event.id} value={event.id}>{event.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Vendor (Optional)</Label>
                <Select 
                  value={formData.vendor_id || "none"} 
                  onValueChange={handleVendorSelect}
                >
                  <SelectTrigger className="input-field h-12" data-testid="expense-vendor-select">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No vendor</SelectItem>
                    {vendors.map(vendor => (
                      <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
                    ))}
                    <SelectItem value="__new__" data-testid="expense-vendor-add-new">+ Add new vendor…</SelectItem>
                  </SelectContent>
                </Select>
                {inlineVendor.open && (
                  <div
                    className="border border-terracotta/30 bg-terracotta/[0.04] rounded-sm p-3 space-y-2"
                    data-testid="inline-vendor-form"
                  >
                    <p className="text-[10px] font-mono uppercase tracking-wider text-terracotta">
                      New vendor
                    </p>
                    <Input
                      value={inlineVendor.name}
                      onChange={(e) => setInlineVendor((p) => ({ ...p, name: e.target.value }))}
                      className="input-field h-10 text-sm"
                      placeholder="Vendor name (e.g., Bombay Florist)"
                      data-testid="inline-vendor-name-input"
                    />
                    <Select
                      value={inlineVendor.category || undefined}
                      onValueChange={(v) => setInlineVendor((p) => ({ ...p, category: v }))}
                    >
                      <SelectTrigger className="input-field h-10 text-sm" data-testid="inline-vendor-category-select">
                        <SelectValue placeholder="Vendor category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2 pt-1">
                      <Button
                        type="button"
                        onClick={() => setInlineVendor({ open: false, name: '', category: '', saving: false })}
                        className="btn-secondary text-xs flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        onClick={handleSaveInlineVendor}
                        disabled={!inlineVendor.name.trim() || !inlineVendor.category.trim() || inlineVendor.saving}
                        className="btn-primary text-xs flex-1"
                        data-testid="inline-vendor-save-btn"
                      >
                        {inlineVendor.saving ? 'Saving…' : 'Save vendor'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="paid"
                    checked={formData.paid}
                    onCheckedChange={(checked) => setFormData({ ...formData, paid: checked })}
                    data-testid="expense-paid-checkbox"
                  />
                  <Label htmlFor="paid" className="text-charcoal text-sm">Mark as paid</Label>
                </div>
                <p className="text-[11px] text-muted leading-relaxed pl-6">
                  Either way, the amount is deducted from your remaining budget. Unpaid expenses appear as <span className="text-ochre">awaiting payment</span>.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="btn-primary flex-1"
                  data-testid="save-expense-btn"
                >
                  {editingExpense ? 'Update' : 'Add'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
  );

  return bare ? content : <Layout>{content}</Layout>;
});
